import { localStorageGetItem } from './local-storage-available';
import { windowIsAvailable } from './window-is-available';

const MODEST_HARDWARE_CONCURRENCY_MAX = 4;
const MODEST_DEVICE_MEMORY_MAX_GB = 4;

/** Override de teste: `localStorage.setItem(FORCE_REDUCE_MOTION_STORAGE_KEY, 'true'|'false')` */
export const FORCE_REDUCE_MOTION_STORAGE_KEY = 'infor:force-reduce-motion';

/**
 * Override de teste via env (Next.js): `NEXT_PUBLIC_FORCE_REDUCE_MOTION=true|false`
 * Ideal para emulador — sem precisar do console.
 */
export const FORCE_REDUCE_MOTION_ENV_KEY = 'NEXT_PUBLIC_FORCE_REDUCE_MOTION';

interface NavigatorWithDeviceMemory extends Navigator {
  readonly deviceMemory?: number;
}

const parseForceOverride = (raw: string | null | undefined): boolean | null => {
  const normalized = (raw ?? '').trim().toLowerCase();

  if (normalized === 'true' || normalized === '1') {
    return true;
  }

  if (normalized === 'false' || normalized === '0') {
    return false;
  }

  return null;
};

const readEnvForceReduceMotionOverride = (): boolean | null => {
  return parseForceOverride(process.env.NEXT_PUBLIC_FORCE_REDUCE_MOTION);
};

const readStorageForceReduceMotionOverride = (): boolean | null => {
  if (!windowIsAvailable()) {
    return null;
  }

  return parseForceOverride(
    localStorageGetItem(FORCE_REDUCE_MOTION_STORAGE_KEY, '')
  );
};

/**
 * Indica se microanimações devem ser reduzidas/desativadas.
 * Critérios (qualquer um): prefers-reduced-motion, CPU <= 4 cores, RAM <= 4 GB.
 * Overrides de teste (prioridade):
 * 1. `NEXT_PUBLIC_FORCE_REDUCE_MOTION` (env — útil no emulador)
 * 2. localStorage `infor:force-reduce-motion`
 * SSR-safe: sem window e sem env force, retorna false.
 */
export const shouldReduceMotion = (): boolean => {
  const envOverride = readEnvForceReduceMotionOverride();

  if (envOverride !== null) {
    return envOverride;
  }

  const storageOverride = readStorageForceReduceMotionOverride();

  if (storageOverride !== null) {
    return storageOverride;
  }

  if (!windowIsAvailable()) {
    return false;
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return true;
  }

  const { hardwareConcurrency } = navigator;

  if (
    typeof hardwareConcurrency === 'number' &&
    hardwareConcurrency > 0 &&
    hardwareConcurrency <= MODEST_HARDWARE_CONCURRENCY_MAX
  ) {
    return true;
  }

  const deviceMemory = (navigator as NavigatorWithDeviceMemory).deviceMemory;

  if (
    typeof deviceMemory === 'number' &&
    deviceMemory > 0 &&
    deviceMemory <= MODEST_DEVICE_MEMORY_MAX_GB
  ) {
    return true;
  }

  return false;
};
